/**
 * Shared utility for formatting entity fields with conditional formatting
 * Supports: booleans, numbers, enums, nested objects/arrays
 */

import React from 'react';
import { CheckCircle, XCircle, ChevronDown, ChevronRight } from 'lucide-react';

export interface FieldMetadata {
  key: string;
  value: any;
  type: 'boolean' | 'number' | 'string' | 'date' | 'enum' | 'array' | 'object' | 'null';
  isNumeric: boolean;
  isBoolean: boolean;
  isEnum: boolean;
  isNested: boolean;
}

/**
 * Detect field type and metadata
 */
export function detectFieldType(key: string, value: any): FieldMetadata {
  if (value === null || value === undefined) {
    return {
      key,
      value,
      type: 'null',
      isNumeric: false,
      isBoolean: false,
      isEnum: false,
      isNested: false,
    };
  }

  if (typeof value === 'boolean') {
    return {
      key,
      value,
      type: 'boolean',
      isNumeric: false,
      isBoolean: true,
      isEnum: false,
      isNested: false,
    };
  }

  if (typeof value === 'number') {
    return {
      key,
      value,
      type: 'number',
      isNumeric: true,
      isBoolean: false,
      isEnum: false,
      isNested: false,
    };
  }

  if (Array.isArray(value)) {
    return {
      key,
      value,
      type: 'array',
      isNumeric: false,
      isBoolean: false,
      isEnum: false,
      isNested: true,
    };
  }

  if (typeof value === 'object') {
    return {
      key,
      value,
      type: 'object',
      isNumeric: false,
      isBoolean: false,
      isEnum: false,
      isNested: true,
    };
  }

  if (typeof value === 'string') {
    // Check if it's a date
    if (key.includes('date') || key.includes('_at') || key.includes('_on')) {
      const dateMatch = value.match(/^\d{4}-\d{2}-\d{2}/);
      if (dateMatch) {
        return {
          key,
          value,
          type: 'date',
          isNumeric: false,
          isBoolean: false,
          isEnum: false,
          isNested: false,
        };
      }
    }

    // Check if it's an enum-like field (short string with limited values)
    const enumKeywords = ['status', 'type', 'zone', 'source', 'priority', 'quality', 'method'];
    if (enumKeywords.some(kw => key.toLowerCase().includes(kw))) {
      return {
        key,
        value,
        type: 'enum',
        isNumeric: false,
        isBoolean: false,
        isEnum: true,
        isNested: false,
      };
    }
  }

  return {
    key,
    value,
    type: 'string',
    isNumeric: false,
    isBoolean: false,
    isEnum: false,
    isNested: false,
  };
}

/**
 * Get color class for boolean values
 */
export function getBooleanColorClass(value: boolean): string {
  return value
    ? 'bg-green-100 text-green-800 border-green-200'
    : 'bg-red-100 text-red-800 border-red-200';
}

/**
 * Get color class for enum values
 */
export function getEnumColorClass(key: string, value: string): string {
  const lowerKey = key.toLowerCase();
  const lowerValue = value.toLowerCase();

  // Status-based colors
  if (lowerKey.includes('status') || lowerKey.includes('validation')) {
    if (lowerValue.includes('valid') || lowerValue.includes('complete') || lowerValue === 'yes') {
      return 'bg-green-100 text-green-800 border-green-200';
    }
    if (lowerValue.includes('fail') || lowerValue.includes('error') || lowerValue === 'no') {
      return 'bg-red-100 text-red-800 border-red-200';
    }
    if (lowerValue.includes('review') || lowerValue.includes('pending') || lowerValue.includes('warning')) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
    if (lowerValue.includes('suspicious')) {
      return 'bg-orange-100 text-orange-800 border-orange-200';
    }
  }

  // Type-based colors
  if (lowerKey.includes('type') || lowerKey.includes('matrix')) {
    const typeColors: Record<string, string> = {
      'groundwater': 'bg-blue-100 text-blue-800 border-blue-200',
      'surface_water': 'bg-cyan-100 text-cyan-800 border-cyan-200',
      'soil': 'bg-amber-100 text-amber-800 border-amber-200',
      'sediment': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'tissue': 'bg-pink-100 text-pink-800 border-pink-200',
      'tap_water': 'bg-indigo-100 text-indigo-800 border-indigo-200',
    };
    return typeColors[lowerValue] || 'bg-gray-100 text-gray-800 border-gray-200';
  }

  // Priority-based colors
  if (lowerKey.includes('priority')) {
    if (lowerValue === '1' || lowerValue.includes('high')) {
      return 'bg-red-100 text-red-800 border-red-200';
    }
    if (lowerValue === '3' || lowerValue.includes('medium')) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
    if (lowerValue === '5' || lowerValue.includes('low')) {
      return 'bg-green-100 text-green-800 border-green-200';
    }
  }

  // Source-based colors
  if (lowerKey.includes('source')) {
    if (lowerValue.includes('raw') || lowerValue.includes('explicit')) {
      return 'bg-green-100 text-green-800 border-green-200';
    }
    if (lowerValue.includes('processed')) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
    if (lowerValue.includes('report')) {
      return 'bg-orange-100 text-orange-800 border-orange-200';
    }
    if (lowerValue === 'none') {
      return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  }

  // Default enum color
  return 'bg-blue-100 text-blue-800 border-blue-200';
}

/**
 * Format a field value for display
 */
export function formatFieldValue(key: string, value: any): string {
  if (value === null || value === undefined) return '-';

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (typeof value === 'number') {
    if (Number.isInteger(value)) {
      return value.toLocaleString();
    }
    // Round to reasonable precision
    const rounded = Math.round(value * 1000) / 1000;
    return rounded.toLocaleString(undefined, {
      maximumFractionDigits: 3,
      minimumFractionDigits: value < 1 ? 3 : 0,
    });
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return '-';
    if (key === 'PFAS_compounds') {
      const detected = value.filter((c: any) => c.concentration_ugL || c.concentration_ugkg);
      return detected.length > 0 ? `${detected.length} compounds` : 'No detections';
    }
    return `${value.length} items`;
  }

  if (typeof value === 'object') {
    const objKeys = Object.keys(value);
    if (objKeys.length === 0) return '-';
    if (objKeys.length <= 3) {
      return objKeys.map(k => `${k}: ${value[k]}`).join(', ');
    }
    return `${objKeys.length} fields`;
  }

  if (key.includes('date') || key.includes('_at') || key.includes('_on')) {
    try {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString();
      }
    } catch {
      // Not a valid date
    }
  }

  return String(value);
}

/**
 * Render a field value with conditional formatting
 */
export function renderFieldValue(
  key: string,
  value: any,
  expanded: boolean = false,
  onToggleExpand?: () => void
): React.ReactNode {
  const fieldMeta = detectFieldType(key, value);

  if (fieldMeta.type === 'null') {
    return <span className="text-gray-400">-</span>;
  }

  if (fieldMeta.isBoolean) {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-xs font-medium ${getBooleanColorClass(value)}`}>
        {value ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
        {value ? 'Yes' : 'No'}
      </span>
    );
  }

  if (fieldMeta.isEnum && typeof value === 'string') {
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded border text-xs font-medium ${getEnumColorClass(key, value)}`}>
        {value}
      </span>
    );
  }

  if (fieldMeta.isNumeric) {
    const formatted = formatFieldValue(key, value);
    return (
      <span className="font-mono text-sm font-semibold text-blue-700 dark:text-blue-400">
        {formatted}
      </span>
    );
  }

  if (fieldMeta.isNested) {
    const canExpand = (Array.isArray(value) && value.length > 0) || 
                     (typeof value === 'object' && Object.keys(value).length > 0);
    
    if (canExpand && onToggleExpand) {
      return (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            title={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-500" />
            )}
          </button>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {Array.isArray(value) 
              ? `${value.length} items` 
              : `${Object.keys(value).length} fields`}
          </span>
        </div>
      );
    }
  }

  return (
    <span className="text-sm text-gray-700 dark:text-gray-300">
      {formatFieldValue(key, value)}
    </span>
  );
}

/**
 * Render expanded nested object/array
 */
export function renderExpandedValue(key: string, value: any): React.ReactNode {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-gray-400 text-sm">Empty array</span>;
    }

    // Special handling for PFAS_compounds
    if (key === 'PFAS_compounds') {
      return (
        <div className="mt-2 space-y-2">
          {value.map((compound: any, idx: number) => (
            <div key={idx} className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-3">
              <div className="font-semibold text-sm text-blue-900 dark:text-blue-100 mb-2">
                {compound.compound_name || `Compound ${idx + 1}`}
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {compound.concentration_ugL !== undefined && (
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Concentration:</span>
                    <span className="ml-2 font-mono font-semibold text-blue-700 dark:text-blue-300">
                      {compound.concentration_ugL.toLocaleString(undefined, { maximumFractionDigits: 3 })} µg/L
                    </span>
                  </div>
                )}
                {compound.concentration_ugkg !== undefined && (
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Concentration:</span>
                    <span className="ml-2 font-mono font-semibold text-blue-700 dark:text-blue-300">
                      {compound.concentration_ugkg.toLocaleString(undefined, { maximumFractionDigits: 3 })} µg/kg
                    </span>
                  </div>
                )}
                {compound.detection_limit_ugL !== undefined && (
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Detection Limit:</span>
                    <span className="ml-2">{compound.detection_limit_ugL} µg/L</span>
                  </div>
                )}
                {compound.detection_limit_ugkg !== undefined && (
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Detection Limit:</span>
                    <span className="ml-2">{compound.detection_limit_ugkg} µg/kg</span>
                  </div>
                )}
                {compound.qualifier && (
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Qualifier:</span>
                    <span className="ml-2">{compound.qualifier}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      );
    }

    // Generic array rendering
    return (
      <div className="mt-2 space-y-1">
        {value.map((item: any, idx: number) => (
          <div key={idx} className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-2 text-xs">
            {typeof item === 'object' ? (
              <pre className="whitespace-pre-wrap text-xs">{JSON.stringify(item, null, 2)}</pre>
            ) : (
              <span>{String(item)}</span>
            )}
          </div>
        ))}
      </div>
    );
  }

  if (typeof value === 'object' && value !== null) {
    const keys = Object.keys(value);
    if (keys.length === 0) {
      return <span className="text-gray-400 text-sm">Empty object</span>;
    }

    return (
      <div className="mt-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-3">
        <table className="min-w-full text-xs">
          <tbody>
            {keys.map((k) => (
              <tr key={k} className="border-b border-gray-200 dark:border-gray-700">
                <td className="py-1 pr-4 font-medium text-gray-600 dark:text-gray-400">
                  {k.replace(/_/g, ' ')}:
                </td>
                <td className="py-1">
                  {renderFieldValue(k, value[k])}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return null;
}

/**
 * Get sort function for a field type
 */
export function getSortFunction(key: string, type: string) {
  return (a: any, b: any) => {
    const aVal = a.data?.[key];
    const bVal = b.data?.[key];

    // Handle nulls
    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;

    if (type === 'number') {
      return (aVal as number) - (bVal as number);
    }

    if (type === 'date') {
      const aDate = new Date(aVal).getTime();
      const bDate = new Date(bVal).getTime();
      return aDate - bDate;
    }

    if (type === 'boolean') {
      return (aVal ? 1 : 0) - (bVal ? 1 : 0);
    }

    // String comparison
    return String(aVal).localeCompare(String(bVal));
  };
}

/**
 * Get filter options for a field
 */
export function getFilterOptions(entities: any[], key: string): { type: string; options: any[] } {
  const fieldMeta = detectFieldType(key, entities[0]?.data?.[key]);
  const values = entities
    .map(e => e.data?.[key])
    .filter(v => v !== null && v !== undefined);

  if (fieldMeta.isBoolean) {
    return {
      type: 'boolean',
      options: ['true', 'false'],
    };
  }

  if (fieldMeta.isNumeric) {
    const numericValues = values.map(v => Number(v)).filter(v => !isNaN(v));
    if (numericValues.length === 0) {
      return { type: 'number', options: [] };
    }

    numericValues.sort((a, b) => a - b);
    const min = numericValues[0];
    const max = numericValues[numericValues.length - 1];
    const q1 = numericValues[Math.floor(numericValues.length * 0.25)];
    const median = numericValues[Math.floor(numericValues.length * 0.5)];
    const q3 = numericValues[Math.floor(numericValues.length * 0.75)];

    return {
      type: 'number',
      options: [
        { label: `All (${min.toFixed(2)} - ${max.toFixed(2)})`, min, max },
        { label: `Low (${min.toFixed(2)} - ${q1.toFixed(2)})`, min, max: q1 },
        { label: `Medium-Low (${q1.toFixed(2)} - ${median.toFixed(2)})`, min: q1, max: median },
        { label: `Medium-High (${median.toFixed(2)} - ${q3.toFixed(2)})`, min: median, max: q3 },
        { label: `High (${q3.toFixed(2)} - ${max.toFixed(2)})`, min: q3, max },
      ],
    };
  }

  if (fieldMeta.isEnum || fieldMeta.type === 'string') {
    const uniqueValues = Array.from(new Set(values.map(v => String(v)))).sort();
    return {
      type: 'enum',
      options: uniqueValues.slice(0, 50), // Limit to 50 options
    };
  }

  return { type: 'string', options: [] };
}

